import { useTranslation } from '../presentation/common/useTranslation.js';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { ShieldX, LogIn, ArrowLeft } from 'lucide-react';
import { useCurrentUser } from '../hooks/useCurrentUser';

/** Maps a requiredRole string to a friendly i18n key */
const ROLE_LABEL_KEY = {
  Adventurer: 'access.role_adventurer',
  GuildAdmin: 'access.role_guildadmin',
  SystemAdmin: 'access.role_systemadmin',
  Partner: 'access.role_partner',
};

/**
 * ProtectedRoute — wraps React Router <Route> elements to enforce auth & role checks.
 *
 * Usage in App.jsx:
 *   <Route element={<ProtectedRoute requiredRole="SystemAdmin" />}>
 *     <Route path="settings" element={<AdminSettings />} />
 *   </Route>
 *
 * Behaviour:
 * - Not logged in         → redirect to /login
 * - Logged in, wrong role → show Unauthorized screen (page does NOT load)
 * - Logged in, right role → render child routes via <Outlet />
 *
 * @param {{ requiredRole: string }} props
 */
export default function ProtectedRoute({ requiredRole }) {
  const { t } = useTranslation();
  const location = useLocation();
  const { isLoggedIn, roles } = useCurrentUser();
  const requiredRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

  // 1. Not logged in → hard redirect to /login
  if (!isLoggedIn) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search + location.hash }} replace />;
  }

  // 2. Role check — user must have the required role in their JWT claims
  const hasRequiredRole = requiredRoles.some(role => roles.includes(role));

  if (!hasRequiredRole) {
    const deniedRole = requiredRoles[0];
    const roleLabelKey = ROLE_LABEL_KEY[deniedRole] ?? deniedRole;
    return <UnauthorizedScreen requiredRoleLabel={t(roleLabelKey)} />;
  }

  // 3. Authorized → render children
  return <Outlet />;
}

/** Full-page unauthorized screen shown before the protected page renders */
function UnauthorizedScreen({ requiredRoleLabel }) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
      <div className="max-w-md w-full space-y-6">
        {/* Icon + Title */}
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="w-16 h-16 bg-rose-950/60 border border-rose-800 flex items-center justify-center">
            <ShieldX className="w-8 h-8 text-rose-400" />
          </div>
          <div>
            <h1 className="text-xl font-black uppercase tracking-tight text-zinc-100">
              {t('access.unauthorized_title')}
            </h1>
            <p className="text-sm text-zinc-400 mt-1">
              {t('access.unauthorized_msg')}
            </p>
          </div>
        </div>

        {/* Required role badge */}
        <div className="bg-zinc-900 border border-zinc-800 p-4 text-center">
          <p className="text-xs text-zinc-500 uppercase tracking-widest font-mono mb-1">
            {t('access.unauthorized_required_role', { role: requiredRoleLabel })}
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <a
            href="/dashboard"
            className="flex items-center justify-center gap-2 px-4 py-3 bg-zinc-800 border border-zinc-700 text-sm font-bold text-zinc-200 hover:bg-zinc-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('access.unauthorized_back_dashboard')}
          </a>
          <a
            href="/login"
            className="flex items-center justify-center gap-2 px-4 py-3 border border-zinc-800 text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors"
          >
            <LogIn className="w-4 h-4" />
            {t('access.unauthorized_back_login')}
          </a>
        </div>
      </div>
    </div>
  );
}
